import { useState, useEffect, useRef, ReactNode } from "react";
import { Home, ShoppingCart, CreditCard, User, Plus, Brain, Leaf, Stethoscope, FileImage, Eye, MoreHorizontal, Timer, Pill, Calendar, FileText, Heart, Baby, MessageCircle, LayoutDashboard, ShoppingBag, Menu, Database, Zap, Award, TrendingUp, Users, Bell, Shield, Activity, BarChart, Clock, LineChart, CheckCircle, Target, Star, Globe, Languages, Info, Mail, Briefcase, Video } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ServicesProductsCarousel } from "@/components/home/ServicesProductsCarousel";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { ToolkitModal } from "@/components/home/ToolkitModal";
import { AuthRedirect } from "@/components/auth/AuthRedirect";
import { visitorMenuItems } from "./navbar/menu/VisitorMenuItems";

interface MobileLayoutProps {
  children?: ReactNode;
}

const MobileLayout = ({
  children
}: MobileLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session,
    isAuthenticated
  } = useAuth();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [toolkitOpen, setToolkitOpen] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const navTabsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    if (location.pathname === '/404') {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  const getActiveNavPath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.includes(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const visible = prevScrollPos > currentScrollPos || currentScrollPos < 10;
      setPrevScrollPos(currentScrollPos);
      setVisible(visible);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  const handleFeatureClick = (path: string, requiresAuth: boolean = false) => {
    if (requiresAuth && !isAuthenticated) {
      toast.error("Vous devez être connecté pour accéder à cette fonctionnalité");
      navigate('/auth');
      setIsMenuOpen(false);
      return;
    }
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleVisitorMenuClick = (item: typeof visitorMenuItems[0]) => {
    if (item.requiresAuth && !isAuthenticated) {
      toast.error("Vous devez vous connecter pour accéder à cette page");
      navigate('/auth');
      setIsMenuOpen(false);
      return;
    }
    navigate(item.path);
    setIsMenuOpen(false);
  };

  const {
    data: profile
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      if (!session?.user.id) return null;
      const {
        data
      } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
      return data;
    },
    enabled: !!session?.user.id
  });

  const {
    data: unreadMessagesCount = 0,
    isError: messagesError
  } = useQuery({
    queryKey: ["unreadMessagesCount"],
    queryFn: async () => {
      if (!session?.user.id) return 0;
      try {
        const {
          data,
          error,
          count
        } = await supabase.from("messages").select("*", {
          count: 'exact'
        }).eq("receiver_id", session.user.id).eq("read", false);
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error("Error fetching messages:", error);
        return 0;
      }
    },
    enabled: !!session?.user.id,
    refetchInterval: 30000
  });

  const {
    data: recentMessages = [],
    isLoading: messagesLoading
  } = useQuery({
    queryKey: ["recentMessages"],
    queryFn: async () => {
      if (!session?.user.id) return [];
      try {
        const {
          data,
          error
        } = await supabase.from("messages").select(`
            *,
            sender:profiles!sender_id(full_name, profile_photo)
          `).eq("receiver_id", session.user.id).order("created_at", {
          ascending: false
        }).limit(5);
        if (error) throw error;
        return data || [];
      } catch (error) {
        console.error("Error fetching messages:", error);
        return [];
      }
    },
    enabled: !!session?.user.id
  });

  const getNavItems = () => {
    const baseItems = [{
      icon: Home,
      label: "Accueil",
      path: "/"
    }, {
      icon: ShoppingBag,
      label: "Marketplace",
      path: "/marketplace-mobile"
    }, {
      icon: MessageCircle,
      label: "Messages",
      path: "/messages-mobile"
    }];
    if (profile?.type === "professional") {
      baseItems.push({
        icon: LayoutDashboard,
        label: "Dashboard",
        path: "/professional-mobile"
      });
    } else {
      baseItems.push({
        icon: FileText,
        label: "Journal",
        path: "/journal-mobile"
      });
    }
    baseItems.push({
      icon: User,
      label: "Profil",
      path: "/profile"
    });
    return baseItems;
  };

  const navItems = getNavItems();

  const features = [{
    icon: Pill,
    label: "Médicaments",
    path: "/medicaments-mobile",
    color: "bg-blue-100 text-blue-600"
  }, {
    icon: Calendar,
    label: "Rendez-vous",
    path: "/rendez-vous-mobile",
    color: "bg-green-100 text-green-600"
  }, {
    icon: FileText,
    label: "Journal",
    path: "/journal-mobile",
    color: "bg-amber-100 text-amber-600"
  }, {
    icon: Heart,
    label: "Cycle menstruel",
    path: "/period-tracking-mobile",
    color: "bg-rose-100 text-rose-600"
  }, {
    icon: Baby,
    label: "Grossesse",
    path: "/gestation-tracking-mobile",
    color: "bg-purple-100 text-purple-600"
  }, {
    icon: Brain,
    label: "Assistance IA",
    path: "/ai-assistance-mobile",
    color: "bg-indigo-100 text-indigo-600"
  }];

  const markAsRead = async (messageId: string) => {
    if (!session?.user.id) return;
    try {
      const {
        error
      } = await supabase.from("messages").update({
        read: true
      }).eq("id", messageId);
      if (error) throw error;
      await Promise.all([queryClient.invalidateQueries({
        queryKey: ["unreadMessagesCount"]
      }), queryClient.invalidateQueries({
        queryKey: ["recentMessages"]
      })]);
    } catch (error) {
      console.error("Error marking message as read:", error);
      toast.error("Impossible de marquer le message comme lu");
    }
  };

  const handleMessageClick = (messageId: string) => {
    markAsRead(messageId);
    navigate("/messages-mobile");
    setIsMessagesOpen(false);
  };

  const isHomePage = location.pathname === '/';

  if (!isMobile) {
    return children || <Outlet />;
  }

  return <div className="min-h-screen bg-medical-gradient relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute inset-0 opacity-10">
          {Array.from({
          length: 15
        }).map((_, i) => <div key={i} className="absolute rounded-full border border-primary/20 opacity-30" style={{
          width: `${20 + Math.random() * 60}px`,
          height: `${20 + Math.random() * 60}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`
        }} />)}
        </div>
        
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div className="w-40 h-40 rounded-full bg-red-500/5 animate-pulse-wave"></div>
          <div className="w-40 h-40 rounded-full bg-red-500/5 animate-pulse-wave" style={{
          animationDelay: '0.5s'
        }}></div>
          <div className="w-40 h-40 rounded-full bg-red-500/5 animate-pulse-wave" style={{
          animationDelay: '1s'
        }}></div>
        </div>
      </div>

      <header className="bg-white/80 backdrop-blur-md p-4 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[80%] p-0 glass-morphism bg-white/90 backdrop-blur-xl">
                <SheetHeader className="px-4 pt-4">
                  {session && profile ? <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg mb-4 shadow-sm">
                      <Avatar className="w-12 h-12 border border-blue-200">
                        <AvatarImage src={profile?.profile_photo} />
                        <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
                          {profile?.full_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold">
                          {profile?.full_name || "Utilisateur"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {profile?.type === "professional" ? "Professionnel de santé" : "Patient"}
                        </p>
                      </div>
                    </div> : <div className="flex items-center justify-between mb-4">
                      <SheetTitle>Menu</SheetTitle>
                      <Link to="/auth" className="text-sm text-blue-600 font-medium">
                        Se connecter
                      </Link>
                    </div>}
                </SheetHeader>
                
                <ScrollArea className="h-[calc(100vh-100px)] px-4">
                  <div className="space-y-6 pb-6">
                    <div className="space-y-2">
                      <h3 className="text-xs uppercase text-gray-500 font-semibold px-1">Navigation</h3>
                      <div className="space-y-1">
                        <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-md transition-colors">
                          <Home className="h-5 w-5 text-blue-600" />
                          <span className="text-sm">Accueil</span>
                        </Link>
                        <Link to="/marketplace-mobile" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-md transition-colors">
                          <ShoppingBag className="h-5 w-5 text-blue-600" />
                          <span className="text-sm">Marketplace</span>
                        </Link>
                        <Link to="/about" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-md transition-colors">
                          <Info className="h-5 w-5 text-blue-600" />
                          <span className="text-sm">À propos</span>
                        </Link>
                        <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-md transition-colors">
                          <Mail className="h-5 w-5 text-blue-600" />
                          <span className="text-sm">Contact</span>
                        </Link>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-xs uppercase text-gray-500 font-semibold px-1">Professionnel</h3>
                      <div className="space-y-1">
                        <Link to="/professional-mobile" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 rounded-md transition-colors">
                          <LayoutDashboard className="h-5 w-5 text-indigo-600" />
                          <span className="text-sm">Dashboard</span>
                        </Link>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-xs uppercase text-gray-500 font-semibold px-1">Compte</h3>
                      <div className="space-y-1">
                        <Link to="/profile" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 p-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-md transition-colors">
                          <User className="h-5 w-5 text-gray-600" />
                          <span className="text-sm">Profile</span>
                        </Link>
                        
                        {isAuthenticated && <button onClick={async () => {
                        await supabase.auth.signOut();
                        setIsMenuOpen(false);
                        navigate('/');
                      }} className="w-full flex items-center gap-3 p-2.5 hover:bg-red-50 rounded-md text-red-600 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                              <polyline points="16 17 21 12 16 7"></polyline>
                              <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            <span className="text-sm">Se déconnecter</span>
                          </button>}
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </SheetContent>
            </Sheet>
          </div>
          
          <div className="flex-1 flex justify-center">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">
                Medicace
              </h1>
            </Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Popover open={isMessagesOpen} onOpenChange={setIsMessagesOpen}>
              <PopoverTrigger asChild>
                <button className="relative rounded-full w-8 h-8 bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white transition-colors" onClick={() => {
                if (isMobile) {
                  navigate("/messages-mobile");
                  setIsMessagesOpen(false);
                }
              }}>
                  <span className="sr-only">Messages</span>
                  <MessageCircle className="w-5 h-5" />
                  {unreadMessagesCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                      {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                    </span>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 backdrop-blur-lg bg-white/90 border border-blue-100" align="end">
                <div className="flex items-center justify-between p-4 border-b border-blue-100">
                  <h3 className="font-medium">Messages</h3>
                  <Link to="/messages-mobile" className="text-xs text-blue-600" onClick={() => setIsMessagesOpen(false)}>
                    Voir tout
                  </Link>
                </div>
                <ScrollArea className="h-[300px]">
                  {messagesLoading ? <div className="flex justify-center items-center h-20">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div> : recentMessages.length > 0 ? <div className="divide-y divide-blue-50">
                      {recentMessages.map((message: any) => <div key={message.id} className={cn("p-3 hover:bg-blue-50/50 transition-colors cursor-pointer", !message.read && "bg-blue-50/50")} onClick={() => handleMessageClick(message.id)}>
                          <div className="flex gap-3">
                            <Avatar className="h-8 w-8 border border-blue-100">
                              <AvatarImage src={message.sender?.profile_photo} alt={message.sender?.full_name} />
                              <AvatarFallback>
                                {message.sender?.full_name?.charAt(0) || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {message.sender?.full_name || "Utilisateur"}
                              </p>
                              <p className="text-xs text-gray-500 truncate mt-1">
                                {message.content}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {format(new Date(message.created_at), 'P', {
                            locale: fr
                          })}
                              </p>
                            </div>
                            {!message.read && <div className="w-2 h-2 rounded-full bg-blue-500 self-start mt-2 animate-pulse"></div>}
                          </div>
                        </div>)}
                    </div> : <div className="flex flex-col items-center justify-center text-center p-6">
                      <MessageCircle className="h-8 w-8 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">Aucun message</p>
                    </div>}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <button className="rounded-full w-8 h-8 bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white transition-colors" onClick={() => navigate("/notifications")}>
              <span className="sr-only">Notifications</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {isHomePage && <>
          <div className="mx-4 mt-4 overflow-hidden relative">
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl text-white overflow-hidden shadow-md relative">
              <div className="p-5 flex items-center justify-between relative z-10">
                <div className="space-y-3 flex-1">
                  <h3 className="font-bold text-xl animate-fade-in">Medicace </h3>
                  <p style={{
                animationDelay: "0.1s"
              }} className="text-blue-100 animate-fade-in mb-3 mx-0 px-0 my-0 py-0 text-base font-normal text-left">Votre compagnon de santé</p>
                  <div className="flex gap-3 mt-1">
                    <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50 rounded-full text-xs px-5 py-2 animate-fade-in font-medium transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105" style={{
                  animationDelay: "0.2s"
                }} onClick={() => setToolkitOpen(true)}>
                      Commencer
                    </Button>
                  </div>
                </div>
                
                <div className="relative flex flex-col items-center w-32 animate-fade-in" style={{
              animationDelay: "0.3s"
            }}>
                  <div className="absolute top-0 right-0 -mt-1 -mr-1">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                      <div className="absolute inset-0 bg-blue-400/30 rounded-full animate-ping" style={{
                    animationDuration: "3s"
                  }}></div>
                      <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-pulse"></div>
                      <div className="bg-blue-500 p-2.5 rounded-full relative z-10 shadow-lg">
                        <Stethoscope className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/90 backdrop-blur-sm mt-10 p-2 rounded-lg shadow-md border border-blue-100 w-full text-slate-950">
                    <div className="flex flex-col items-center">
                      <span className="text-blue-600 font-bold text-lg">99%</span>
                      <span className="text-blue-500 text-xs">Satisfaction</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 h-8 overflow-hidden opacity-30">
                <svg width="100%" height="100%" viewBox="0 0 100 20" preserveAspectRatio="none">
                  <path d="M0,10 L10,10 C15,10 15,5 20,5 C25,5 25,15 30,15 C35,15 35,5 40,5 C45,5 45,10 50,10 L60,10 C65,10 65,5 70,5 C75,5 75,15 80,15 C85,15 85,5 90,5 C95,5 95,10 100,10" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1" className="animate-pulse" />
                </svg>
              </div>
            </div>
          </div>

          <div className="mt-6 px-4">
            <div className="grid grid-cols-3 gap-4">
              {features.map((feature, index) => <button key={index} onClick={() => handleFeatureClick(feature.path)} className="flex flex-col items-center cursor-pointer focus:outline-none transition-all duration-300 hover:scale-105" aria-label={`Accéder à ${feature.label}`}>
                  <div className={cn("w-14 h-14 rounded-full flex items-center justify-center mb-2 shadow-md", feature.color)}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <span className="text-xs text-center font-medium">{feature.label}</span>
                </button>)}
            </div>
          </div>

          <div className="mt-6 px-4 mb-8">
            <ServicesProductsCarousel />
          </div>

          
        </>}

      <div className="relative z-10 pb-16">
        {children || <Outlet />}
      </div>

      {visible && <nav className="bg-white/80 backdrop-blur-md shadow-lg fixed inset-x-0 bottom-0 z-50 border-t border-gray-100">
          <div className="grid grid-cols-5 gap-1">
            {navItems.map((item, idx) => <Link key={item.label} to={item.path} className={cn("flex flex-col items-center justify-center py-2 px-1 transition-colors", getActiveNavPath(item.path) ? "text-primary" : "text-gray-500 hover:text-primary")} ref={el => navTabsRef.current[idx] = el}>
                <item.icon className="h-5 w-5" />
                <span className="text-xs mt-1">{item.label}</span>
                {getActiveNavPath(item.path) && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />}
              </Link>)}
          </div>
        </nav>}
      
      <ToolkitModal isOpen={toolkitOpen} onClose={() => setToolkitOpen(false)} />
    </div>;
};

export default MobileLayout;
